// **D4** — the colony rail's names and placements are derived, and never guessed.
//
// Nothing shipped publishes `colony → member`, and nothing publishes a **per-round** placement:
// `MemberStandingDto` carries a tournament-wide `rank` over the mean, which is a different
// number about a different thing. Both are therefore derived here, from one specified and tested
// server invariant:
//
//   > Colony ids are assigned **in join-ordinal order over competing members** when membership
//   > closes, and never move (`repository.rs`'s `MembershipClose`).
//
// **The derivation is cross-checked and fails safe.** The phase read states the caller's own
// `colonyId`; if the derivation disagrees with it, the invariant has changed under us and the
// rail names colonies by **number only**. Mislabelling one player's colony with another player's
// name is the one outcome worse than an incomplete rail — the same reasoning as the
// absent-subject rule in **D3**.
//
// Recorded as a deferral rather than a silent derivation: a one-field `colonyId` on
// `MemberStandingDto`, and/or a per-round placement, would delete both derivations, and both
// belong in `rounds-tournament`'s lane.

import { scoreForRound, type MemberStanding, type Standings } from '../../net/rounds-standings';

export interface ColonyIdentity {
  colony: number;
  /** The member's display name, or `null` when the derivation is not trusted. */
  displayName: string | null;
  /** The member's player id, or `null` when the derivation is not trusted. */
  player: number | null;
}

export interface ColonyPlacement {
  colony: number;
  displayName: string | null;
  /** The **G1** score for the round, or `null` when the round is not graded for this colony. */
  score: number | null;
  /** 1-based placement in the round, ties sharing a number. `null` when unscored. */
  placement: number | null;
  /** Whether another colony shares this placement. */
  shared: boolean;
}

export interface ColonyRoster {
  /**
   * Whether display names may be shown at all.
   *
   * `false` is not an error state — the rail still lists every colony, still orders by placement
   * and still shows every score. What it withholds is *whose* colony each one is.
   */
  namesTrusted: boolean;
  /** Every competing colony, in colony-id order. */
  colonies: readonly ColonyIdentity[];
  /** That round's placements, ordered by placement. */
  placementsFor(roundNo: number): ColonyPlacement[];
  /** The caller's own placement for the round, or `null` if they have no colony. */
  ownPlacementFor(roundNo: number): ColonyPlacement | null;
}

export interface ColonyRosterInput {
  standings: Standings;
  /** The caller's own colony id, as the **phase read** states it. The cross-check's other half. */
  ownColonyId: number | null;
  /** The caller's own join ordinal, as the phase read states it. */
  ownJoinOrdinal: number | null;
}

export function deriveColonyRoster(input: ColonyRosterInput): ColonyRoster {
  // Competing members, in join order. `competing` is `memberKind !== 'spectator'`, so the host
  // is in — the server's own `is_competing`, and getting this wrong would shift every colony id
  // by one.
  const competing: MemberStanding[] = input.standings.members
    .filter((member) => member.competing)
    .slice()
    .sort((a, b) => a.joinOrdinal - b.joinOrdinal);

  const derivedIndex = competing.findIndex(
    (member) => member.joinOrdinal === input.ownJoinOrdinal,
  );

  // The canary. A contradiction requires *both* halves to be observable: a spectator has no
  // colony id at all, so there is nothing to disagree with, and an unobservable check is not a
  // failed one — the derivation depends on the server's assignment rule, not on the caller.
  const namesTrusted =
    input.ownColonyId === null ? true : derivedIndex >= 0 && derivedIndex === input.ownColonyId;

  const colonies: ColonyIdentity[] = competing.map((member, colony) => ({
    colony,
    displayName: namesTrusted ? member.displayName : null,
    player: namesTrusted ? member.player : null,
  }));

  const placementsFor = (roundNo: number): ColonyPlacement[] => {
    const rows = competing.map((member, colony) => ({
      colony,
      displayName: namesTrusted ? member.displayName : null,
      score: scoreForRound(member, roundNo),
    }));

    // Score descending; ties broken by ascending colony id — the same "lowest colony id"
    // tie-break DESIGN §2 uses. An unscored colony sorts last rather than as a zero.
    const ordered = rows.slice().sort((a, b) => {
      if (a.score === b.score) {
        return a.colony - b.colony;
      }
      if (a.score === null) {
        return 1;
      }
      if (b.score === null) {
        return -1;
      }
      return b.score - a.score;
    });

    // Standard competition ranking: equal scores **share** a placement and the next one skips.
    // The alternative — numbering ties 1 and 2 by colony id — would present an order the round
    // did not produce as though it had.
    const scoreCounts = new Map<number, number>();
    for (const row of ordered) {
      if (row.score !== null) {
        scoreCounts.set(row.score, (scoreCounts.get(row.score) ?? 0) + 1);
      }
    }
    let placement = 0;
    let previousScore: number | null = null;
    let assigned: number | null = null;
    return ordered.map((row) => {
      if (row.score === null) {
        return { ...row, placement: null, shared: false };
      }
      placement += 1;
      if (previousScore !== row.score) {
        assigned = placement;
        previousScore = row.score;
      }
      return {
        ...row,
        placement: assigned,
        shared: (scoreCounts.get(row.score) ?? 0) > 1,
      };
    });
  };

  return {
    namesTrusted,
    colonies,
    placementsFor,
    ownPlacementFor(roundNo: number): ColonyPlacement | null {
      if (input.ownColonyId === null) {
        return null;
      }
      return placementsFor(roundNo).find((row) => row.colony === input.ownColonyId) ?? null;
    },
  };
}
